import { Injectable, inject, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { ToastService } from './toast.service';

/** Room facts the shared header renders while the user is inside a room. */
export interface RoomChromeInfo {
  roomName: string;
  joinCode: string;
  hasPassword: boolean;
  playerCount: number;
  maxPlayers: number;
}

/**
 * Bridge between the room page and the shared MainLayout header: the room
 * page publishes its identity here (name, invite code, capacity) so the
 * header can render the room block and a LEAVE button; the header's actions
 * flow back through this service.
 */
@Injectable({ providedIn: 'root' })
export class RoomChromeService {
  private toastService = inject(ToastService);

  /** Set while the user is inside a room; null everywhere else. */
  readonly roomInfo = signal<RoomChromeInfo | null>(null);

  /** Emitted when the header's LEAVE button is pressed. */
  readonly leaveRequested = new Subject<void>();

  setRoomInfo(info: RoomChromeInfo): void {
    this.roomInfo.set(info);
  }

  clear(): void {
    this.roomInfo.set(null);
  }

  requestLeave(): void {
    this.leaveRequested.next();
  }

  /** Copy the current room's invite link to the clipboard. */
  async copyRoomLink(): Promise<void> {
    const info = this.roomInfo();
    if (!info) return;
    const link = `${window.location.origin}/room/${info.joinCode}`;

    // Return focus to document body so Phaser keyboard listeners are not blocked.
    (document.activeElement as HTMLElement | null)?.blur();

    try {
      await this.writeToClipboard(link);
      this.toastService.success('Room link copied!');
    } catch (err) {
      console.error('Failed to copy room link:', err);
      this.toastService.error('Failed to copy room link');
    }
  }

  /**
   * Copy text to the clipboard with a fallback for insecure contexts.
   *
   * The async Clipboard API (`navigator.clipboard`) is only available in secure
   * contexts (HTTPS or localhost). In production served over plain HTTP it is
   * `undefined`, so we fall back to a hidden-textarea + `execCommand('copy')`.
   */
  private async writeToClipboard(text: string): Promise<void> {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    // Keep it out of view and prevent scrolling/zoom on mobile.
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    textarea.setAttribute('readonly', '');
    document.body.appendChild(textarea);

    try {
      textarea.select();
      const succeeded = document.execCommand('copy');
      if (!succeeded) {
        throw new Error('execCommand("copy") returned false');
      }
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
